import { useMap } from "@vis.gl/react-maplibre";
import { FC, PropsWithChildren, useEffect, useState } from "react";

import { MapZoomContext } from "../map-zoom.context";
import { MAP_INITIAL_ZOOM } from "../map.constants";
import { getAliveMap } from "./maplibre-map.utils";

/**
 * Owns the map's only `zoomend` subscription and shares the zoom with every layer below it — the MapLibre
 * twin of `MapZoomProvider`, filling the same context so consumers do not care which engine is mounted.
 */
export const MapLibreZoomProvider: FC<PropsWithChildren> = ({ children }) => {
  const { current: mapRef } = useMap();
  const [zoom, setZoom] = useState(
    () => getAliveMap(mapRef)?.getZoom() ?? MAP_INITIAL_ZOOM,
  );

  useEffect(() => {
    const map = getAliveMap(mapRef);
    if (!map) {
      return;
    }

    const syncZoom = (): void => {
      const aliveMap = getAliveMap(mapRef);
      if (!aliveMap) {
        return;
      }

      setZoom(aliveMap.getZoom());
    };

    syncZoom();
    map.on("zoomend", syncZoom);
    return () => {
      getAliveMap(mapRef)?.off("zoomend", syncZoom);
    };
  }, [mapRef]);

  return (
    <MapZoomContext.Provider value={zoom}>{children}</MapZoomContext.Provider>
  );
};
